/**
 * FhirAllergyListWidget — Allergy alerts for subject dashboard (DRR-321)
 *
 * Shows allergies and intolerances from FHIR AllergyIntolerance resources.
 * Critical for medication safety — highlighted with warning colors.
 */

import React, { useMemo } from 'react';
import {
  Stack,
  Text,
  Badge,
  Group,
  Card,
  Skeleton,
  Alert,
  Box,
  ScrollArea,
  ThemeIcon,
} from '@mantine/core';
import { IconAlertTriangle, IconAlertCircle, IconShieldCheck } from '@tabler/icons-react';
import { useFhirAllergiesBySubjectQuery } from '@ferp/core/generated/graphql';
import type { WidgetProps } from '@ferp/core';

interface ParsedAllergy {
  id: string;
  substance: string;
  clinicalStatus: string;
  verificationStatus: string;
  type: string;
  category?: string[];
  criticality?: string;
  reactions?: Array<{
    manifestation: string;
    severity?: string;
  }>;
  recordedDate?: string;
}

function parseAllergy(resourceId: string, json: string): ParsedAllergy | null {
  try {
    const f = JSON.parse(json) as Record<string, unknown>;

    const codeCC = f.code as { text?: string; coding?: Array<{ display?: string }> } | undefined;
    const substance = codeCC?.text ?? codeCC?.coding?.[0]?.display ?? 'Unknown allergen';

    const clinicalCC = f.clinicalStatus as { coding?: Array<{ code?: string }> } | undefined;
    const clinicalStatus = clinicalCC?.coding?.[0]?.code ?? 'unknown';

    const verifCC = f.verificationStatus as { coding?: Array<{ code?: string }> } | undefined;
    const verificationStatus = verifCC?.coding?.[0]?.code ?? 'unknown';

    const categories = f.category as string[] | undefined;

    const rawReactions = f.reaction as Array<{
      manifestation?: Array<{ text?: string; coding?: Array<{ display?: string }> }>;
      severity?: string;
    }> | undefined;

    const reactions = rawReactions?.map(r => ({
      manifestation: r.manifestation?.[0]?.text ?? r.manifestation?.[0]?.coding?.[0]?.display ?? 'Unknown',
      severity: r.severity,
    }));

    return {
      id: resourceId,
      substance,
      clinicalStatus,
      verificationStatus,
      type: (f.type as string) ?? 'allergy',
      category: categories,
      criticality: f.criticality as string | undefined,
      reactions,
      recordedDate: f.recordedDate as string | undefined,
    };
  } catch {
    return null;
  }
}

const CRITICALITY_COLOR: Record<string, string> = {
  high: 'red',
  low: 'yellow',
  'unable-to-assess': 'orange',
};

const SEVERITY_COLOR: Record<string, string> = {
  severe: 'red',
  moderate: 'orange',
  mild: 'yellow',
};

export function FhirAllergyListWidget({ subjectActorId }: WidgetProps) {
  const { data, loading, error } = useFhirAllergiesBySubjectQuery({
    variables: { subjectActorId: subjectActorId ?? '' },
    skip: !subjectActorId,
    fetchPolicy: 'cache-and-network',
  });

  const allergies = useMemo(() => {
    const raw = data?.fhirAllergiesBySubject ?? [];
    return raw
      .filter(r => r.isLatest !== false)
      .map(r => parseAllergy(r.resourceId, r.fhirJson))
      .filter((a): a is ParsedAllergy => a !== null);
  }, [data]);

  if (loading && allergies.length === 0) {
    return (
      <Stack gap="sm">
        <Skeleton height={50} />
        <Skeleton height={50} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Error loading allergies.
      </Alert>
    );
  }

  if (allergies.length === 0) {
    return (
      <Alert icon={<IconShieldCheck size={16} />} color="green" variant="light">
        No known allergies documented.
      </Alert>
    );
  }

  const activeAllergies = allergies.filter(a => a.clinicalStatus === 'active');
  const highCriticalityCount = activeAllergies.filter(a => a.criticality === 'high').length;

  return (
    <Stack gap="sm">
      <Group gap="xs">
        <ThemeIcon size="sm" color="red" variant="light">
          <IconAlertTriangle size={14} />
        </ThemeIcon>
        <Text fw={600} size="sm">Allergies</Text>
        <Badge size="sm" color="red">{activeAllergies.length}</Badge>
        {highCriticalityCount > 0 && (
          <Badge size="sm" color="red" variant="filled">{highCriticalityCount} High Risk</Badge>
        )}
      </Group>

      <ScrollArea.Autosize mah={200}>
        <Stack gap="xs">
          {activeAllergies.map((allergy) => (
            <Card
              key={allergy.id}
              withBorder
              padding="xs"
              radius="sm"
              style={{
                borderColor: allergy.criticality === 'high' ? 'var(--mantine-color-red-4)' : undefined,
                borderWidth: allergy.criticality === 'high' ? 2 : 1,
              }}
            >
              <Group justify="space-between" wrap="nowrap">
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Group gap="xs" wrap="nowrap">
                    <IconAlertTriangle
                      size={14}
                      color={allergy.criticality === 'high' ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-orange-6)'}
                    />
                    <Text fw={500} size="sm" lineClamp={1}>{allergy.substance}</Text>
                  </Group>
                  <Group gap="xs" mt={4}>
                    {allergy.category?.map((cat) => (
                      <Badge key={cat} size="xs" variant="outline" color="gray">{cat}</Badge>
                    ))}
                    {allergy.reactions?.slice(0, 2).map((r, i) => (
                      <Badge
                        key={i}
                        size="xs"
                        variant="light"
                        color={r.severity ? SEVERITY_COLOR[r.severity] ?? 'gray' : 'gray'}
                      >
                        {r.manifestation}
                      </Badge>
                    ))}
                  </Group>
                </Box>
                {allergy.criticality && (
                  <Badge size="xs" color={CRITICALITY_COLOR[allergy.criticality] ?? 'gray'}>
                    {allergy.criticality}
                  </Badge>
                )}
              </Group>
            </Card>
          ))}
        </Stack>
      </ScrollArea.Autosize>
    </Stack>
  );
}
